<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import LaisGamificationSurface from 'dashboard/components-next/LaisGamificationSurface.vue';
import LaisHeader from 'dashboard/components-next/LaisHeader.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import RealEstateFinanceBoletoDialog from '../components/RealEstateFinanceBoletoDialog.vue';
import RealEstateFinanceBoletoFeedback from '../components/RealEstateFinanceBoletoFeedback.vue';
import RealEstateFinancePaymentDialog from '../components/RealEstateFinancePaymentDialog.vue';
import RealEstateFinanceStatusUpdateForm from '../components/RealEstateFinanceStatusUpdateForm.vue';
import { useRealEstateFinancePayments } from '../composables/useRealEstateFinancePayments';
import { useRealEstateFinanceInstallment } from '../composables/useRealEstateFinanceInstallment';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');
const installmentId = computed(() => route.params.installmentId);
const finance = useRealEstateFinanceInstallment({
  installmentId,
  notify: useAlert,
  t,
});
const statusBoletoFeedbackVisible = ref(false);
const statusBoletoQuantity = ref(0);
const {
  boletoDialogVisible,
  boletoFeedbackVisible,
  boletoUploadCount,
  closeBoletoFeedback,
  confirmPayment,
  downloadBoleto,
  handlePayment,
  openBoleto,
  paymentDialogVisible,
  removeBoleto,
  saveBoleto,
  selectedResponsibility,
  uploadBoleto,
} = useRealEstateFinancePayments({ finance, onSuggestedStatus: () => {} });

const backToFinance = () =>
  router.push({
    name: 'operacao_imobiliaria_financeiro',
    params: { accountId: route.params.accountId },
  });
const backToStatus = () => router.back();
const boletoQuantity = () =>
  (
    finance.selectedInstallment.value?.parcela?.responsaveis_pagamento || []
  ).filter(
    responsibility =>
      responsibility.boleto?.enviado &&
      responsibility.data_emissao &&
      responsibility.data_envio &&
      responsibility.data_validade
  ).length;
const updateStatus = async payload => {
  try {
    await finance.transitionInstallment(payload);
    if (payload.target_status === 'boleto_enviado') {
      statusBoletoQuantity.value = boletoQuantity();
      statusBoletoFeedbackVisible.value = true;
      return true;
    }
    backToStatus();
    return true;
  } catch {
    return false;
  }
};
</script>

<template>
  <LaisGamificationSurface
    :user-id="currentUser?.id"
    show-rail
    class="min-h-full bg-lais-bg-soft"
  >
    <LaisHeader
      :title="t('REAL_ESTATE_FINANCE.STATUS_UPDATE.TITLE')"
      :subtitle="t('REAL_ESTATE_FINANCE.STATUS_UPDATE.SUBTITLE')"
      :breadcrumb="[
        { label: t('REAL_ESTATE_FINANCE.TITLE') },
        { label: t('REAL_ESTATE_FINANCE.STATUS_UPDATE.TITLE') },
      ]"
      show-back
      :back-label="t('REAL_ESTATE_FINANCE.STATUS_UPDATE.BACK')"
      @back="backToFinance"
    />
    <main class="px-4 pt-5 sm:px-6 lg:px-10">
      <p
        v-if="finance.error.value"
        class="mx-auto w-full max-w-[1042px] rounded-lais border border-lais-danger/30 bg-white p-4 text-sm text-lais-danger"
      >
        {{ finance.error.value }}
      </p>
      <div
        v-else-if="finance.loadingDetail.value"
        class="mx-auto w-full max-w-[1042px] space-y-5"
      >
        <div class="h-52 animate-pulse rounded-lais bg-white" />
        <div class="h-56 animate-pulse rounded-lais bg-white" />
      </div>
      <RealEstateFinanceStatusUpdateForm
        v-else
        :installment="finance.selectedInstallment.value"
        :loading="Boolean(finance.actionLoading.value)"
        :statuses="finance.statusCatalog.value"
        @back="backToStatus"
        @boleto="openBoleto"
        @payment="handlePayment"
        @submit="updateStatus"
      />
    </main>
    <RealEstateFinancePaymentDialog
      :show="paymentDialogVisible"
      :responsibility="selectedResponsibility"
      :loading="Boolean(finance.actionLoading.value)"
      @close="paymentDialogVisible = false"
      @confirm="confirmPayment"
    />
    <RealEstateFinanceBoletoDialog
      :show="boletoDialogVisible"
      :responsibility="selectedResponsibility"
      :loading="Boolean(finance.actionLoading.value)"
      @close="boletoDialogVisible = false"
      @upload="uploadBoleto"
      @save="saveBoleto"
      @remove="removeBoleto"
      @download="downloadBoleto"
    />
    <RealEstateFinanceBoletoFeedback
      :show="boletoFeedbackVisible"
      :quantity="boletoUploadCount"
      @close="closeBoletoFeedback"
    />
    <RealEstateFinanceBoletoFeedback
      :show="statusBoletoFeedbackVisible"
      :quantity="statusBoletoQuantity"
      @close="statusBoletoFeedbackVisible = false"
    />
  </LaisGamificationSurface>
</template>
